import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2 } from "lucide-react";

const sel = "w-full min-h-[44px] rounded-xl border border-slate-200 bg-white px-3 text-sm";

function Q({ n, title, children }) {
  return (
    <fieldset className="rounded-2xl border border-slate-200 bg-white p-4">
      <legend className="px-1 text-sm font-semibold text-slate-900">{n}. {title}</legend>
      <div className="mt-2 space-y-2">{children}</div>
    </fieldset>
  );
}

function Check({ checked, onChange, label, testid }) {
  return (
    <label className="flex items-center gap-3 min-h-[44px] text-sm text-slate-800 cursor-pointer">
      <Checkbox checked={checked} onCheckedChange={(v) => onChange(!!v)} className="h-5 w-5" data-testid={testid} />{label}
    </label>
  );
}

function FieldsQ({ lookups, account, fields, setFields }) {
  const toggle = (k, on) => setFields(on ? [...fields, { field: k, reported_value: account[lookups.report_fields[k]] || "", correct_value: "" }] : fields.filter((f) => f.field !== k));
  return Object.entries(lookups.fields).map(([k, label]) => {
    const f = fields.find((x) => x.field === k);
    return (
      <div key={k}>
        <Check checked={!!f} onChange={(on) => toggle(k, on)} label={label} testid={`facts-field-${k}`} />
        {f && (
          <div className="grid sm:grid-cols-2 gap-2 pl-8 pb-2">
            <Input value={f.reported_value} readOnly={!!account[lookups.report_fields[k]]} placeholder="What the report shows" className="min-h-[44px]"
              onChange={(e) => setFields(fields.map((x) => (x.field === k ? { ...x, reported_value: e.target.value } : x)))} data-testid={`facts-reported-${k}`} />
            <Input value={f.correct_value} placeholder="What it should show" className="min-h-[44px]"
              onChange={(e) => setFields(fields.map((x) => (x.field === k ? { ...x, correct_value: e.target.value } : x)))} data-testid={`facts-correct-${k}`} />
          </div>
        )}
      </div>
    );
  });
}

function RecipientQ({ card, facts, set }) {
  if (card.recipient_type === "bureau") {
    return (
      <select className={sel} value={facts.recipient_bureau || ""} onChange={(e) => set({ recipient_bureau: e.target.value })} data-testid="facts-bureau">
        <option value="">Choose a credit bureau</option><option value="equifax">Equifax</option><option value="experian">Experian</option><option value="transunion">TransUnion</option>
      </select>
    );
  }
  if (!card.required_fields.includes("recipient_address")) return <p className="text-xs text-slate-500">No mailing address is needed for this narrative.</p>;
  const r = facts.recipient || {};
  const upd = (k, v) => set({ recipient: { ...r, [k]: v } });
  return (
    <div className="grid sm:grid-cols-2 gap-2">
      {[["name", "Company name"], ["address_line1", "Street address"], ["city", "City"], ["state", "State"], ["zip_code", "ZIP code"]].map(([k, ph]) => (
        <Input key={k} value={r[k] || ""} placeholder={ph} onChange={(e) => upd(k, e.target.value)} className="min-h-[44px]" data-testid={`facts-recipient-${k}`} />
      ))}
    </div>
  );
}

export function FactsForm({ card, lookups, account, history, busy, errors, onSubmit, onCancel }) {
  const [facts, setFacts] = useState({ requested_action: card.allowed_actions[0], evidence: [], explanation: "" });
  const [fields, setFields] = useState([]);
  const recorded = history?.latest_result ? { date: history.latest_response_date || "", result: history.latest_result, response_summary: "" } : null;
  const [prior, setPrior] = useState(card.required_fields.includes("prior_dispute") ? (recorded || { date: "", result: "", response_summary: "" }) : null);
  const [length, setLength] = useState("standard");
  const set = (p) => setFacts((f) => ({ ...f, ...p }));
  const ev = facts.evidence;
  const submit = () => onSubmit({ ...facts, disputed_fields: fields, prior_dispute: prior || undefined }, length);
  return (
    <div className="space-y-3" data-testid="facts-form">
      <Q n={1} title="Which account is this about?"><p className="text-sm text-slate-800" data-testid="facts-account">{account.creditor} {account.account_number_masked} · {account.account_type} · {account.account_status}</p></Q>
      <Q n={2} title="What information is inaccurate or incomplete? What does the report show, and what should it show?">
        <FieldsQ lookups={lookups} account={account} fields={fields} setFields={setFields} />
      </Q>
      <Q n={3} title="Explain the error in your own words">
        <Textarea rows={4} value={facts.explanation} onChange={(e) => set({ explanation: e.target.value })} placeholder="Describe the specific facts: what happened, when, and why the reporting is wrong." data-testid="facts-explanation" />
      </Q>
      <Q n={4} title="What evidence supports your position?">
        {Object.entries(lookups.evidence).map(([k, label]) => {
          const item = ev.find((x) => x.type === k);
          return (
            <div key={k}>
              <Check checked={!!item} label={label} testid={`facts-evidence-${k}`} onChange={(on) => set({ evidence: on ? [...ev, { type: k, description: "" }] : ev.filter((x) => x.type !== k) })} />
              {item && <Input className="ml-8 w-[calc(100%-2rem)] min-h-[44px]" value={item.description} placeholder="Short description (optional)" onChange={(e) => set({ evidence: ev.map((x) => (x.type === k ? { ...x, description: e.target.value } : x)) })} />}
            </div>
          );
        })}
      </Q>
      <Q n={5} title="Was this account disputed before? What response did you receive?">
        <Check checked={!!prior} onChange={(on) => setPrior(on ? { date: "", result: "", response_summary: "" } : null)} label="Yes, I disputed it before" testid="facts-prior-toggle" />
        {prior && recorded && <p className="text-xs text-emerald-700" data-testid="facts-prior-recorded">Filled in from the response you recorded on the Documents page. Your recorded result is used.</p>}
        {prior && (
          <div className="grid sm:grid-cols-2 gap-2">
            <Input type="date" value={prior.date} onChange={(e) => setPrior({ ...prior, date: e.target.value })} className="min-h-[44px]" data-testid="facts-prior-date" />
            <select className={sel} value={prior.result} onChange={(e) => setPrior({ ...prior, result: e.target.value })} data-testid="facts-prior-result">
              <option value="">Result received</option>{Object.entries(lookups.prior_results).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <Input className="sm:col-span-2 min-h-[44px]" value={prior.response_summary} placeholder="What the response said (optional)" onChange={(e) => setPrior({ ...prior, response_summary: e.target.value })} />
          </div>
        )}
      </Q>
      <Q n={6} title="What correction or investigation are you requesting?">
        <select className={sel} value={facts.requested_action} onChange={(e) => set({ requested_action: e.target.value })} data-testid="facts-action">
          {card.allowed_actions.map((a) => <option key={a} value={a}>{lookups.actions[a]}</option>)}
        </select>
      </Q>
      <Q n={7} title="Who is this letter going to?"><RecipientQ card={card} facts={facts} set={set} /></Q>
      <ExtraQ card={card} facts={facts} set={set} />
      <Q n={9} title="Letter length">
        <select className={sel} value={length} onChange={(e) => setLength(e.target.value)} data-testid="facts-length">{lookups.lengths.map((l) => <option key={l} value={l}>{l[0].toUpperCase() + l.slice(1)}</option>)}</select>
      </Q>
      {errors?.length > 0 && <ul className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800 list-disc pl-6" data-testid="facts-errors">{errors.map((e) => <li key={e}>{e}</li>)}</ul>}
      <div className="flex flex-col-reverse sm:flex-row gap-2">
        <Button variant="outline" onClick={onCancel} className="min-h-[44px] rounded-xl" data-testid="facts-cancel">Back to library</Button>
        <Button onClick={submit} disabled={busy} className="min-h-[44px] rounded-xl btn-primary" data-testid="facts-generate">{busy ? <Loader2 className="animate-spin" size={16} /> : "Generate my letter"}</Button>
      </div>
    </div>
  );
}

function ExtraQ({ card, facts, set }) {
  const items = [];
  if (card.required_fields.includes("duplicate_of")) items.push(<Input key="d" className="min-h-[44px]" value={facts.duplicate_of || ""} placeholder="Which other listing is the duplicate?" onChange={(e) => set({ duplicate_of: e.target.value })} data-testid="facts-duplicate" />);
  if (card.required_fields.includes("date_of_first_delinquency")) items.push(<Input key="o" type="date" className="min-h-[44px]" value={facts.date_of_first_delinquency || ""} onChange={(e) => set({ date_of_first_delinquency: e.target.value })} data-testid="facts-dofd" />);
  if (["identity_theft_block", "identity_theft_records"].includes(card.letter_key)) items.push(<Check key="i" checked={!!facts.identity_theft_declared} onChange={(v) => set({ identity_theft_declared: v })} label="I declare that I am a victim of identity theft and did not open or authorize this account." testid="facts-idtheft" />);
  if (card.required_fields.includes("account_paid")) items.push(<Check key="p" checked={!!facts.account_paid} onChange={(v) => set({ account_paid: v })} label="I confirm this account is paid." testid="facts-paid" />);
  if (["cfpb_narrative", "state_ag_narrative"].includes(card.letter_key)) items.push(<Check key="u" checked={!!facts.issue_unresolved} onChange={(v) => set({ issue_unresolved: v })} label="I confirm the issue remains unresolved after my disputes." testid="facts-unresolved" />);
  return items.length ? <Q n={8} title="Confirm these details">{items}</Q> : null;
}
